/**
 * nodeModel — per-node model/vendor derivation, shared by the graph and the
 * pipeline detail page.
 *
 * The model an agent runs on is chosen PER NODE, on the graph (each node that
 * runs a model carries a small "model" sub-node; clicking it picks the vendor +
 * model + key). This module is the ONE place that decides, from data alone,
 * what a node's model state IS — so the disc, the trigger gate and the
 * sub-agent check can never disagree.
 *
 * Storage (mirrors self-host exactly):
 *   • vendor + model → the workflow row's `nodeConfigOverrides[<nodeId>]`
 *     ({agent, model}); the runner folds them into the run's
 *     `config.models[<node>]` / `config.agents[<node>]`, which are the top of
 *     the engine's own resolution chains. The ENGINE is untouched.
 *   • the KEY → per VENDOR, never per node: the agent's own env (a snapshot
 *     the runtime reads FIRST) and, optionally, the project's shared
 *     credential.
 *
 * Nothing here names an agent, a slug or a template.
 */
import { dispatchedSlugsOf } from './dispatchMarker';

// The env vars that carry a vendor's credential on an agent. First entry is
// what we WRITE; the rest are recognised when reading an existing snapshot.
export const VENDOR_ENV_KEYS = {
  claude: ['CLAUDE_CODE_OAUTH_TOKEN', 'ANTHROPIC_API_KEY', 'CLAUDE_CODE_CREDENTIALS_JSON'],
  codex: ['CODEX_API_KEY', 'OPENAI_API_KEY'],
  gemini: ['GEMINI_API_KEY', 'GOOGLE_API_KEY'],
};

export const VENDOR_LABELS = { claude: 'Claude', codex: 'Codex', gemini: 'Gemini' };

/** The node's serialized config from the graph (may be absent). */
export const nodeCfgOf = (graph, id) => {
  const c = graph?.nodeConfigs?.[id];
  return c && typeof c === 'object' ? c : null;
};

/** The node's code, however the serializer shipped it. */
const nodeCodeOf = (cfg) => {
  const s = cfg?.sourceCode || cfg?.executeCode || cfg?.customCode || '';
  return typeof s === 'string' ? s : '';
};

/**
 * Does this node invoke a model at all? A node with a prompt does; so does a
 * custom-execute node whose code calls invokeAgent. A SUB-GRAPH DISPATCH node
 * does NOT — it runs another agent, and that agent owns its own nodes' models
 * (set on the child's own page).
 */
export function nodeRunsModel(cfg) {
  if (!cfg) return false;
  // The marker is a STRING or an ARRAY OF STRINGS (a roster node declares every
  // member it dispatches on ONE node) — ask the normalizer, never `typeof`.
  if (dispatchedSlugsOf(cfg.dispatchesWorkflow).length) return false;
  if (typeof cfg.prompt === 'string' && cfg.prompt) return true;
  return nodeCodeOf(cfg).indexOf('invokeAgent') !== -1;
}

// A custom-execute node can hand its own `model:` to invokeAgent (a cheap
// triage tier, say), and that beats the run's model — so the operator's pick
// would not take effect and the control must be read-only.
const INVOKE_MODEL_RE = /invokeAgent\s*\([\s\S]{0,400}?\bmodel\s*:/;
// …and when what it passes is a plain STRING LITERAL we can name it, so the
// disc says `haiku-4.5` instead of the useless "fixed". An expression
// (`resolveTriageModel(state)` — a fixed cheap TIER whose vendor follows the
// run) is unknowable from here, and we say so rather than guess.
const INVOKE_MODEL_LITERAL_RE = /invokeAgent\s*\([\s\S]{0,400}?\bmodel\s*:\s*['"]([^'"]{1,64})['"]/;

/**
 * Is this node's model FIXED BY THE TEMPLATE'S CODE?
 *
 * NOTE the `overrides` argument: the graph a detail page renders has the USER's
 * per-node overrides already MERGED into nodeConfigs (so the node editor shows
 * saved edits). Reading `cfg.agent` blind therefore reports the operator's OWN
 * pick as "fixed in code" — which renders the node locked and unclickable the
 * moment they set it. A vendor present in the override map came from the
 * operator and is not a lock.
 *
 * @returns {{vendor: string|null, model: boolean, modelName: string}|null}
 *          null when not fixed. `modelName` is '' unless the code names a
 *          literal model we can read off.
 */
export function nodeModelLock(cfg, override, nodeId, modelBlocks) {
  if (!cfg) return null;
  const userPickedVendor = typeof override?.agent === 'string' && override.agent.trim();
  const vendor = !userPickedVendor && typeof cfg.agent === 'string' && cfg.agent.trim()
    ? cfg.agent.trim()
    : null;
  let codeModel = false;
  let modelName = '';
  try {
    const code = nodeCodeOf(cfg);
    codeModel = INVOKE_MODEL_RE.test(code);
    if (codeModel) {
      const m = code.match(INVOKE_MODEL_LITERAL_RE);
      // 'auto' names nothing — it IS the inherit case, so it would be a lie on
      // a disc that claims the model is fixed.
      if (m && m[1] && m[1] !== 'auto') modelName = m[1];
      // A bare `model:` in the code is NOT a lock by itself. The engine's chain
      // puts the operator's per-node pin ABOVE options.model
      // (resolveInvocationModel), so a code node FORWARDING a runtime value
      // (`model: state?.model`) is overridable like any other node — a padlock
      // there denies a picker that would actually work. A lock is claimable
      // only when the code names a LITERAL, or the template DECLARES the pin as
      // data (fixedModelByVendor, joined by role) — the deliberate-tier cases.
      if (!modelName && !hasDeclaredFixedModel(modelBlocks, nodeId)) {
        codeModel = false;
      }
    }
  } catch { codeModel = false; }
  if (!vendor && !codeModel) return null;
  return { vendor, model: codeModel, modelName };
}

/**
 * Does the template declare a fixed model for this node at all (any vendor)?
 * The vendor-specific alias is resolved later by declaredFixedModel — this
 * presence check is what upgrades a code `model:` into a genuine lock.
 */
export function hasDeclaredFixedModel(modelBlocks, nodeId) {
  if (!Array.isArray(modelBlocks) || !nodeId) return false;
  const block = modelBlocks.find((b) => b && b.role === nodeId);
  const byVendor = block?.fixedModelByVendor;
  return !!(byVendor && typeof byVendor === 'object' && Object.keys(byVendor).length);
}

/** This node's saved pick, as {agent?, model?} ({} when it inherits). */
export function nodeModelPick(override) {
  const out = {};
  if (typeof override?.agent === 'string' && override.agent.trim()) out.agent = override.agent.trim();
  if (typeof override?.model === 'string' && override.model.trim()) out.model = override.model.trim();
  return out;
}

/**
 * Does a credential exist for `vendor`?
 *
 * The AGENT'S OWN ENV is checked FIRST and is sufficient on its own — that is
 * what the RUNTIME does (agent env beats the project credential). Judging by
 * the project alone marks a node red the moment the page reloads after saving
 * a key to the agent, and again whenever someone removes a project credential
 * the agent had stopped depending on.
 *
 * @param {object} agentEnvKeys  {ENV_NAME: lastFour|true} — the agent's env
 * @param {object} projectSecrets the project's masked secrets
 * @returns {boolean} — true when unknown (never cry wolf)
 */
export function vendorHasKey(vendor, agentEnvKeys, projectSecrets, projectVendor) {
  if (!vendor) return true;
  if (agentEnvKeys) {
    const names = VENDOR_ENV_KEYS[vendor] || [];
    for (const n of names) if (agentEnvKeys[n]) return true;
  }
  if (!projectSecrets && !agentEnvKeys) return true; // nothing loaded yet
  if (!projectSecrets) return false;
  return !!projectSecretFor(vendor, projectSecrets, projectVendor);
}

/** The project's (masked) credential for a vendor, or ''. */
export function projectSecretFor(vendor, secrets, projectVendor) {
  const s = secrets || {};
  const str = (x) => (typeof x === 'string' && x ? x : '');
  const isPrimary = vendor === projectVendor;
  if (vendor === 'codex') return str(s.codexApiKey) || (isPrimary ? str(s.aiApiKey) : '');
  if (vendor === 'gemini') return str(s.geminiApiKey) || (isPrimary ? str(s.aiApiKey) : '');
  return str(s.aiOAuthToken) || str(s.aiApiKey); // claude
}

/** The agent's own snapshot for a vendor: its lastFour, `true`, or ''. */
export function agentSnapshotFor(vendor, agentEnvKeys) {
  const names = VENDOR_ENV_KEYS[vendor] || [];
  for (const n of names) {
    const hit = agentEnvKeys?.[n];
    if (hit) return hit;
  }
  return '';
}

/**
 * The model a code-LOCKED node will actually run, when the template DECLARES it.
 *
 * A node whose code picks its own model usually picks a cheap TIER per vendor
 * (`FAST_TRIAGE_MODEL_BY_VENDOR[resolveRunVendor(state)]`), so the concrete
 * alias is unknowable from the graph: what a deployed row carries is
 * `customExecute.toString()` — the function's OWN text, not the module around
 * it, so the map and the resolver it calls are simply absent.
 *
 * We don't guess at it. The template already publishes the answer as DATA: a
 * `models[]` block with `fixedModelByVendor` (the SAME frozen map the node asks
 * the vendor for, so the two cannot drift). Join it to the node by `role`.
 *
 * @param {object[]} modelBlocks  marketplace.models
 * @returns {string} the alias, or '' when nothing is declared for this node
 */
export function declaredFixedModel(modelBlocks, nodeId, vendor) {
  if (!Array.isArray(modelBlocks) || !nodeId || !vendor) return '';
  const block = modelBlocks.find((b) => b && b.role === nodeId);
  const byVendor = block?.fixedModelByVendor;
  if (!byVendor || typeof byVendor !== 'object') return '';
  const m = byVendor[vendor];
  return typeof m === 'string' && m && m !== 'auto' ? m : '';
}

/**
 * The one vendor every saved per-node pick agrees on, or '' when the picks
 * disagree (or there are none). Display-only inference — the LAST link of the
 * locked-node vendor chain, never a substitute for the real declarations.
 */
function unanimousPinnedVendor(overrides) {
  if (!overrides || typeof overrides !== 'object') return '';
  let seen = '';
  for (const k of Object.keys(overrides)) {
    const a = typeof overrides[k]?.agent === 'string' ? overrides[k].agent.trim() : '';
    if (!a) continue;
    if (seen && seen !== a) return '';
    seen = a;
  }
  return seen;
}

/**
 * One node's model STATE — the single source the disc, the trigger gate and
 * the sub-agent check all read.
 *
 * @returns {{kind:'locked'|'ready'|'nokey'|'unset', vendor:string, label:string, sub:string}}
 */
export function nodeModelState({
  cfg, nodeId, override, agentEnvKeys, projectSecrets, projectVendor, modelBlocks,
  agentVendor, overrides,
}) {
  const lock = nodeModelLock(cfg, override, nodeId, modelBlocks);
  if (lock) {
    // WHICH vendor this node actually runs on. Code that fixes the model
    // usually fixes a cheap TIER, not a vendor (`FAST_TRIAGE_MODEL_BY_VENDOR[
    // resolveRunVendor(state)]`) — so the vendor is still inherited, and the
    // disc must show the one that will really be used. The chain mirrors the
    // runtime's own resolution (the executor stamps AGENT_TYPE from the row's
    // aiAgent; a tier-fixed node's resolveRunVendor() reads exactly that):
    // the template's pin, this node's own pick, the AGENT-level default
    // (`agentVendor` — the vendor the executor stamps as AGENT_TYPE for the
    // process these nodes run in: the row's own aiAgent, or, for a dispatched
    // sub-agent whose row declares nothing, the OWNING parent's resolved
    // vendor — agentComposition.inheritedAgentVendor — because an in-process
    // child dispatch runs inside the parent's container under the parent's
    // AGENT_TYPE; its omission is what left a locked disc a bare "Fixed" on a
    // bare child page / a project with no default vendor), then the
    // project default. Last, display-only inference: the one vendor every
    // OTHER node's saved pick agrees on — an operator who pinned Claude on
    // the other nodes has answered the question even without an agent
    // default. Disagreeing pins prove nothing, so they resolve nothing.
    const vendor = lock.vendor
      || (typeof override?.agent === 'string' ? override.agent.trim() : '')
      || agentVendor
      || projectVendor
      || unanimousPinnedVendor(overrides)
      || '';
    // Say the most specific TRUE thing: a literal the code names, then the
    // model the TEMPLATE declares for this node + vendor, then just "Fixed".
    // The padlock already carries "you can't change this" — the caption
    // shouldn't spend its one line repeating it.
    const declared = declaredFixedModel(modelBlocks, nodeId, vendor);
    // A fixed MODEL still needs a KEY. The template chose which model runs
    // here; it cannot supply the credential to run it — so a locked node with
    // no credential for its vendor is exactly as dead as an unset one, and has
    // to say so and be clickable. (It was neither: read-only and skipped by the
    // trigger check, which left no way at all to give it a key.)
    const missingKey = !vendorHasKey(vendor, agentEnvKeys, projectSecrets, projectVendor);
    return {
      kind: 'locked',
      vendor,
      label: lock.modelName
        || declared
        || (lock.model ? 'Fixed' : (VENDOR_LABELS[vendor] || vendor || 'Fixed')),
      sub: missingKey ? `no ${VENDOR_LABELS[vendor] || vendor} key` : '',
      missingKey,
    };
  }
  const pick = nodeModelPick(override);
  if (pick.agent || pick.model) {
    const vendor = pick.agent || '';
    const label = pick.model || VENDOR_LABELS[vendor] || vendor;
    if (!vendorHasKey(vendor, agentEnvKeys, projectSecrets, projectVendor)) {
      return { kind: 'nokey', vendor, label, sub: `no ${VENDOR_LABELS[vendor] || vendor} key` };
    }
    return { kind: 'ready', vendor, label, sub: '' };
  }
  return { kind: 'unset', vendor: '', label: 'Set model', sub: '' };
}

/**
 * Nodes of this graph whose model is still UNSET — model-running, not locked,
 * no saved pick. These are the "apply the same model to all" candidates: a
 * node that already carries a pick is never listed (never overwritten), a
 * locked node (literal or template-declared) is never listed (never touched),
 * and a node whose declared block is an EMBEDDING is excluded — its pick is
 * agent state (embeddingModel), not a node override, so a chat {agent, model}
 * written there would claim an LLM pick on a node that runs none.
 *
 * @param {string} [excludeId] the node whose save triggered the question —
 *        it just received its pick, so it is neither "other" nor unset.
 */
export function unsetModelNodes({ graph, overrides, modelBlocks, excludeId = null }) {
  const out = [];
  for (const n of (graph?.nodes || [])) {
    if (!n || !n.id || n.id === excludeId) continue;
    const cfg = nodeCfgOf(graph, n.id);
    if (!nodeRunsModel(cfg)) continue;
    const block = Array.isArray(modelBlocks)
      ? modelBlocks.find((b) => b && b.role === n.id)
      : null;
    if (block?.kind === 'embedding') continue;
    const override = overrides?.[n.id];
    if (nodeModelLock(cfg, override, n.id, modelBlocks)) continue;
    const pick = nodeModelPick(override);
    if (!pick.agent && !pick.model) out.push(n.id);
  }
  return out;
}

/**
 * Nodes of this graph that cannot run yet: no model picked, or a model whose
 * vendor has no credential anywhere.
 *
 * A code-LOCKED node is NOT ready by construction — the template fixed which
 * model runs, not who pays for it, so one whose vendor has no key fails the
 * run exactly like an unset node. Skipping them here is what made a fixed node
 * a dead end: unclickable AND never flagged.
 */
export function unreadyNodes({
  graph, overrides, agentEnvKeys, projectSecrets, projectVendor, modelBlocks,
  agentVendor,
}) {
  const out = [];
  for (const n of (graph?.nodes || [])) {
    const cfg = nodeCfgOf(graph, n.id);
    if (!nodeRunsModel(cfg)) continue;
    const st = nodeModelState({
      cfg,
      nodeId: n.id,
      override: overrides?.[n.id],
      agentEnvKeys,
      projectSecrets,
      projectVendor,
      modelBlocks,
      agentVendor,
      overrides,
    });
    if (st.kind === 'unset') out.push({ id: n.id, why: 'no model' });
    else if (st.kind === 'nokey' || st.missingKey) out.push({ id: n.id, why: 'no key' });
  }
  return out;
}
